import React, { useCallback, useState, useEffect } from 'react';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { EntitySelectionCriteriaBuilder } from './EntitySelectionCriteriaBuilder';
import { RelatedEntityFiltersBuilder } from './RelatedEntityFiltersBuilder';
import {
  TieredEntitySelectionInput,
  EntitySelectionCriteriaInput,
  RelatedEntityConfigInput,
  EntityCriteriaCompoundOperatorInput,
  InputMaybe,
} from '../../../../types/generated-types';
import { QueryTester } from './QueryTester';
import { v4 as uuIdV4 } from 'uuid';

const TabPanel = ({
  children,
  value,
  index,
  style,
  ...other
}: {
  children?: React.ReactNode;
  index: number;
  value: number;
  style?: React.CSSProperties;
}) => (
  <div
    style={style ? style : {}}
    role="tabpanel"
    hidden={value !== index}
    id={`entity-selection-tab-panel-${index}`}
    aria-labelledby={`entity-selection-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const EntitySelectionBuilder = ({
  value,
  onUpdateRequest,
  targetModel,
  relatedEntityFilters,
  tiers = 1,
}: {
  value: EntityCriteriaCompoundOperatorInput;
  onUpdateRequest: (
    updatedValue: EntityCriteriaCompoundOperatorInput,
    updatedRelatedEntityFilters: RelatedEntityConfigInput[],
  ) => void;
  relatedEntityFilters:
    | (RelatedEntityConfigInput | InputMaybe<RelatedEntityConfigInput>)[]
    | undefined
    | null;
  targetModel: string;
  tiers?: number;
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [model, setModel] = useState<EntityCriteriaCompoundOperatorInput>({
    ...value,
  });
  const [filters, setFilters] = useState<RelatedEntityConfigInput[]>([]);
  const [testModel, setTestModel] = useState<TieredEntitySelectionInput | null>(
    null,
  );

  useEffect(() => {
    const newFilters = (relatedEntityFilters ?? [])
      .filter((filter) => !!filter)
      .map((filter) => filter as RelatedEntityConfigInput);
    setFilters(newFilters);
  }, [relatedEntityFilters]);

  useEffect(() => {
    let myTestModel: TieredEntitySelectionInput | null = null;
    if (model && targetModel) {
      myTestModel = {
        basisName: targetModel,
        selectionCriteria: model,
        targetModel: {
          modelName: targetModel,
          relatedEntities: filters,
        },
        tiers,
        id: uuIdV4(),
      };
    }
    setTestModel(myTestModel);
  }, [model, filters, targetModel, tiers]);

  // When selection criteria changes, notify parent with updated selection structure
  const handleCriteriaChange = useCallback(
    (criteria: EntitySelectionCriteriaInput) => {
      const newModel = {
        ...model,
        criteria: criteria,
      };
      setModel(newModel);
      setTimeout(() => {
        onUpdateRequest(newModel, filters);
      }, 100);
    },
    [filters, model, onUpdateRequest],
  );

  const handleFiltersChange = useCallback(
    (updatedFilters: RelatedEntityConfigInput[]) => {
      setFilters(updatedFilters);
      setTimeout(() => {
        onUpdateRequest(model, updatedFilters);
      }, 100);
    },
    [model, onUpdateRequest],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={(_event, newValue) => setTabValue(newValue)}
          aria-label="Filter Definition Tabs"
        >
          <Tab label="Attribute Filters" />
          <Tab label="Relationship Filters" />
          <Tab label="Testing" />
        </Tabs>
      </Box>
      <TabPanel
        style={{
          height: 'calc(100vh - 535px)',
          overflow: 'scroll',
        }}
        value={tabValue}
        index={0}
      >
        <EntitySelectionCriteriaBuilder
          criteria={model.criteria}
          onChange={handleCriteriaChange}
          targetModel={targetModel}
        />
      </TabPanel>
      <TabPanel
        style={{
          height: 'calc(100vh - 535px)',
          overflow: 'scroll',
        }}
        value={tabValue}
        index={1}
      >
        <RelatedEntityFiltersBuilder
          targetModel={targetModel}
          relatedEntityFilters={filters}
          onRelatedEntityFiltersChange={handleFiltersChange}
          tiers={tiers}
        />
      </TabPanel>
      <TabPanel
        style={{
          height: 'calc(100vh - 510px)',
          overflow: 'scroll',
        }}
        value={tabValue}
        index={2}
      >
        {testModel ? (
          <QueryTester value={testModel} />
        ) : (
          <div>No Test Model Available</div>
        )}
      </TabPanel>
    </Box>
  );
};
